import * as React from 'react';

import { compose, getDisplayName, setDisplayName } from 'recompose';

import Retracked from 'js/lib/retracked';
import useRouter from 'js/lib/useRouter';

import ProgramHomeDomainSubdomainBody, {
  ProgramHomeDomainSubdomainBodyPlaceholder,
} from 'bundles/enterprise-collections/components/ProgramHomeDomainSubdomainBody';
import { NotFoundContainer } from 'bundles/enterprise-collections/components/ProgramHomeDomainSubdomainContainer';
import ProgramHomeDomainSubdomainHeader, {
  ProgramHomeDomainSubdomainHeaderPlaceholder,
} from 'bundles/enterprise-collections/components/ProgramHomeDomainSubdomainHeader';
import { enterpriseDomainsIndexConfig } from 'bundles/enterprise-learner-search/components/search/enterpriseIndexConfig';
import ProgramMiniModal, {
  closeMiniModalWithRoute,
  useRouterParams,
} from 'bundles/enterprise-legacy-xdp/components/ProgramMiniModal';
import { getProductPath, launchMiniModal } from 'bundles/enterprise-legacy-xdp/hocs/withMiniModal';
import type { Product, ProductType } from 'bundles/program-common/types/programCommon';
import type { PropsFromEnterpriseHomeApp } from 'bundles/program-home/components/enterprise-home/EnterpriseHomeApp';
import SearchProvider from 'bundles/search-common/providers/SearchProvider';

// This style file is for only importing the critical css for performance
import 'css!bundles/program-home/components/enterprise-home/__styles__/EnterpriseDomainsPage';

export type Props = Pick<
  PropsFromEnterpriseHomeApp,
  | 'thirdPartyOrganization'
  | 'domains'
  | 'subdomains'
  | 'loading'
  | 'userId'
  | 'isBrowseOnlyProgram'
  | 'shouldShowSponsoredByMessage'
  | 'programIds'
>;

export const EnterpriseDomainsPage = ({
  thirdPartyOrganization,
  domains,
  subdomains,
  loading,
  userId,
  isBrowseOnlyProgram,
  shouldShowSponsoredByMessage,
  programIds,
}: Props) => {
  const router = useRouter();
  const onCloseMiniModal = React.useCallback(() => closeMiniModalWithRoute(router), [router]);
  const onProductCardClick = React.useCallback(
    (product: Product, productType: ProductType, collectionId?: string | null) => {
      launchMiniModal(router, product.id, productType === 'course', collectionId || '', undefined, undefined, {
        slug: product.slug,
        productPagePath: getProductPath(product),
      });
    },
    [router]
  );

  const domainSlug = router.params.domainSlug;
  const subdomainSlug = router.params.subdomainSlug as string | undefined;
  const domain = domains.find((x) => x.id === domainSlug || x.slug === domainSlug);
  const subdomain =
    subdomainSlug && domain
      ? subdomains.find((x) => (x.id === subdomainSlug || x.slug === subdomainSlug) && x.domainId === domain.id)
      : undefined;

  // ---

  // Not a react hook, just a poorly aged identifier.
  const routerParams = useRouterParams(router);

  if (loading) {
    return (
      <main>
        <ProgramHomeDomainSubdomainHeaderPlaceholder />
        <ProgramHomeDomainSubdomainBodyPlaceholder />
      </main>
    );
  }

  if (
    !thirdPartyOrganization ||
    !userId ||
    !domain ||
    // program-home urls have this weird "all-XXX" subdomain slug that's garbage and ignored, so ignore it here too
    (subdomainSlug && !subdomainSlug.startsWith('all-') && !subdomain)
  ) {
    return <NotFoundContainer />;
  }

  // ProgramHomeDomainSubdomainHeader relies on a query that embeds subdomains in domains
  let domainForHeader: $TSFixMe = domain;
  const subdomainsForDomain = domain
    ? subdomains.filter((x) => x.domainId === domain.id && x.id !== domain.id)
    : undefined;
  if (subdomains) {
    domainForHeader = { ...domain, subdomains: { elements: subdomainsForDomain } };
  }

  const domainId = domain.id;
  const subdomainId = subdomain?.id;

  return (
    <>
      <main>
        <SearchProvider
          // key to refresh API when subdomain change
          key={`${domainId}:${subdomainId}`}
          searchConfigs={enterpriseDomainsIndexConfig({
            searchContext: { searchInPrograms: { programIds } },
            domainId,
            subdomainId,
          })}
        >
          <ProgramHomeDomainSubdomainHeader
            domain={domainForHeader}
            subdomain={subdomain}
            thirdPartyOrgSlug={thirdPartyOrganization.slug}
            thirdPartyOrganizationId={thirdPartyOrganization.id}
          />
          <ProgramHomeDomainSubdomainBody
            domain={domain}
            isOrgHome
            onProductCardClick={onProductCardClick}
            subdomain={subdomain}
            thirdPartyOrganizationId={thirdPartyOrganization.id}
            userId={userId}
          />
        </SearchProvider>
      </main>
      {routerParams && (
        <ProgramMiniModal
          thirdPartyOrganizationName={thirdPartyOrganization.name}
          renderCourseHomeLink
          onCloseMiniModal={onCloseMiniModal}
          isUpswell={false}
          hideAction={isBrowseOnlyProgram}
          disableSponsoredByMessage={!shouldShowSponsoredByMessage}
          thirdPartyOrgId={thirdPartyOrganization.id}
          spookyMultiprogramFlag
          {...routerParams}
        />
      )}
    </>
  );
};

export default compose<Props, Props>(
  setDisplayName(getDisplayName(EnterpriseDomainsPage)),
  Retracked.createTrackedContainer(() => ({
    namespace: {
      app: 'program_home_v3',
      page: 'enterprise_domains_page',
    },
  }))
)(EnterpriseDomainsPage);
